
/**
 * pdp-area-gate.js
 *
 * PDP-only. Loaded only when request.page_type == 'product'. Must load
 * AFTER warehouse-area.js and area-selector.js.
 *
 * ── Fix log (see chat history for full diagnosis) ────────────────────────
 * v3: fixed event.preventDefault() not stopping Hyper's own product-form
 *     submit listener (needed stopImmediatePropagation too), added real
 *     disabled-state gating for out-of-stock (Requirement 4), and forced
 *     hide/show via inline !important style instead of relying solely on
 *     the `hidden` attribute (theme CSS could override it).
 * v4: gate buttons now include the loading-spinner markup for structural
 *     parity with the real buttons, and the Buy Now gate button uses
 *     btn--primary (not btn--secondary) to match the theme's own
 *     visual-weight convention — the real product-form__submit button
 *     demotes itself to btn--secondary specifically when the dynamic
 *     checkout button is present, implying the dynamic checkout button
 *     is meant to carry the primary visual weight in that pair.
 * v5: card-level Add to Cart / Choose Options button now mirrors the
 *     sold-out/enabled state already computed for the sibling Buy Now
 *     button on the same card — same computation (based on p.id only,
 *     no all-variant check), same trigger points (init + DOM scan).
 *     No new area-change listeners added; Add to Cart simply piggybacks
 *     on whatever recomputes Buy Now's state.
 *
 * Stock data source: per-variant warehouse stock is rendered into the DOM
 * at page load via Liquid (see 09-buy-buttons-stock-data-patch.txt) as a
 * JSON blob keyed by variant ID. Read synchronously — no network call,
 * no token, no async race conditions.
 *
 * Dependencies (read-only, never reimplemented):
 *   window.WarehouseArea.getResolvedArea()
 *   window.WarehouseArea.NO_AREA_SELECTED
 *   window.ensureAreaSelected()
 *   window.openAreaModal()
 */

(function () {
  "use strict";

  if (typeof window.WarehouseArea === "undefined") {
    console.error(
      "[pdp-area-gate] WarehouseArea module not found. Load warehouse-area.js first."
    );
    return;
  }
  if (typeof window.ensureAreaSelected !== "function") {
    console.error(
      "[pdp-area-gate] ensureAreaSelected() not found. Load area-selector.js first."
    );
    return;
  }

  var WA = window.WarehouseArea;

  var SELECTORS = {
    form: '[data-type="add-to-cart-form"]',
    submitButton: '[type="submit"][name="add"]',
    variantIdInput: 'input[name="id"].product-variant-id',
    dynamicCheckoutWrapper: "[data-dynamic-checkout-wrapper]",
    customBuyNow: ".buy-now",
    inventoryBlock: '[id^="Inventory-"]',
    cardAddToCartButton: ".add-to-cart"
  };

  var STOCK_DATA_ELEMENT_ID = "warehouse-stock-data";

  var OUT_OF_STOCK_MESSAGE_TEMPLATE =
    "This product is not available in {area_name}. Please select a different area or check back later.";

  var _stockDataCache = new WeakMap();

  // function getStockDataForForm(scopeEl) {
  //   if (_stockDataCache.has(scopeEl)) {
  //     return _stockDataCache.get(scopeEl);
  //   }

  //   var wrapper = scopeEl.closest(".product-form, .ct-item");
  //   var el =
  //     (scopeEl.querySelector && scopeEl.querySelector("script[data-warehouse-stock]")) ||
  //     (wrapper && wrapper.querySelector("script[data-warehouse-stock]")) ||
  //     (wrapper && wrapper.querySelector("#" + STOCK_DATA_ELEMENT_ID)) ||
  //     document.getElementById(STOCK_DATA_ELEMENT_ID);

  //   var data;
  //   if (!el) {
  //     console.warn("[area-gate] No warehouse stock data found for this element. Failing open.");
  //     data = {};
  //   } else {
  //     try {
  //       data = JSON.parse(el.textContent || "{}");
  //     } catch (err) {
  //       console.error("[area-gate] Failed to parse warehouse stock JSON.", err);
  //       data = {};
  //     }
  //   }

  //   _stockDataCache.set(scopeEl, data);
  //   return data;
  // }
  function getStockDataForForm(scopeEl) {
    if (_stockDataCache.has(scopeEl)) {
      return _stockDataCache.get(scopeEl);
    }

    var wrapper = scopeEl.closest(".product-form, .ct-item");
    var container = scopeEl.closest(".product__info-container, .product__info-wrapper");

    var el =
      (scopeEl.querySelector && scopeEl.querySelector("script[data-warehouse-stock]")) ||
      (wrapper && wrapper.querySelector("script[data-warehouse-stock]")) ||
      (container && container.querySelector("script[data-warehouse-stock]")) ||
      (wrapper && wrapper.querySelector("#" + STOCK_DATA_ELEMENT_ID)) ||
      document.getElementById(STOCK_DATA_ELEMENT_ID);

    var data;
    if (!el) {
      console.warn("[area-gate] No warehouse stock data found for this element. Failing open.");
      data = {};
    } else {
      try {
        data = JSON.parse(el.textContent || "{}");
      } catch (err) {
        console.error("[area-gate] Failed to parse warehouse stock JSON.", err);
        data = {};
      }
    }

    _stockDataCache.set(scopeEl, data);
    return data;
  }

  function resolveStockForVariant(variantId, warehouse, formEl) {
    var data = getStockDataForForm(formEl);
    var variantEntry = data[String(variantId)];

    if (variantEntry === undefined) {
      console.warn(
        "[pdp-area-gate] No stock data found for variant " +
          variantId +
          ". Is the metafield set? Failing open."
      );
      return { ok: false, reason: "missing_variant_data" };
    }

    if (variantEntry === null) {
      console.warn(
        "[pdp-area-gate] Stock metafield is empty for variant " +
          variantId +
          ". Failing open."
      );
      return { ok: false, reason: "empty_variant_metafield" };
    }

    var rawValue = variantEntry[warehouse];

    if (rawValue === undefined || rawValue === null) {
      return { ok: true, quantity: 0 };
    }

    if (typeof rawValue !== "number") {
      console.warn(
        "[pdp-area-gate] Unexpected non-numeric stock value for variant " +
          variantId +
          ", warehouse " +
          warehouse +
          ":",
        rawValue,
        "Failing open."
      );
      return { ok: false, reason: "invalid_value_type" };
    }

    return { ok: true, quantity: rawValue };
  }

  function getVariantIdElement(formEl) {
    return (
      formEl.querySelector(SELECTORS.variantIdInput) ||
      formEl.querySelector('select[name="id"]')
    );
  }

  function getCurrentVariantId(formEl) {
    var el = formEl ? getVariantIdElement(formEl) : document.querySelector(SELECTORS.variantIdInput);
    return el ? el.value : null;
  }

  function forceHide(el) {
    if (!el) return;
    el.hidden = true;
    el.style.setProperty("display", "none", "important");
  }

  function forceShow(el) {
    if (!el) return;
    el.hidden = false;
    el.style.removeProperty("display");
  }

  function getSoldOutLabel(el) {
    return el.getAttribute("data-sold-out-text") || "Sold out";
  }

  function getTextEl(el) {
    return (
      el.querySelector(".product-card__action-text") ||
      el.querySelector(".btn__text") ||
      el // no wrapper span at all — swap the button's own text directly
    );
  }

  function ourDisable(el) {
    if (!el) return;

    if (el.disabled) {
      return;
    }

    el.disabled = true;
    el.setAttribute("data-area-gate-disabled", "true");

    var textEl = getTextEl(el);
    if (textEl) {
      el.setAttribute("data-area-gate-original-text", textEl.textContent);
      textEl.textContent = getSoldOutLabel(el);
    }
  }

  function ourEnable(el) {
    if (!el) return;
    if (el.getAttribute("data-area-gate-disabled") !== "true") return;

    el.disabled = false;
    el.removeAttribute("data-area-gate-disabled");

    var textEl = getTextEl(el);
    var originalText = el.getAttribute("data-area-gate-original-text");
    if (textEl && originalText !== null) {
      textEl.textContent = originalText;
    }
    el.removeAttribute("data-area-gate-original-text");
  }

  function getGateMode(formEl) {
    return formEl.getAttribute("data-area-gate-mode") === "label" ? "label" : "message";
  }

  function getInventoryBlockForForm(formEl) {
    var container = formEl.closest(".product__info-container");
    if (!container) return null;
    return container.querySelector(SELECTORS.inventoryBlock);
  }

  function updateInventoryLabel(formEl, quantity, areaName) {
    var blockEl = getInventoryBlockForForm(formEl);
    if (!blockEl) return;

    var textEl = blockEl.querySelector(".product__inventory-text");
    var pEl = blockEl.querySelector(".product__inventory");
    if (!textEl || !pEl) return;

    pEl.classList.remove(
      "product__inventory--in-stock",
      "product__inventory--low-stock",
      "product__inventory--out-of-stock"
    );

    if (quantity > 0) {
      pEl.classList.add("product__inventory--in-stock");
      textEl.textContent = "In stock in " + areaName;
    } else {
      pEl.classList.add("product__inventory--out-of-stock");
      textEl.textContent = "Out of stock in " + areaName;
    }

    blockEl.setAttribute("data-area-override", "true");
  }

  function clearInventoryLabelOverride(formEl) {
    var blockEl = getInventoryBlockForForm(formEl);
    if (blockEl) blockEl.removeAttribute("data-area-override");
  }

  function showBlockedMessage(formEl, message) {
    if (formEl.hasAttribute("data-suppress-gate-message")) return;

    var existing = formEl.querySelector("[data-area-gate-message]");
    if (existing) existing.remove();

    var msgEl = document.createElement("p");
    msgEl.setAttribute("data-area-gate-message", "true");
    msgEl.className = "product__inventory product__inventory--out-of-stock";
    msgEl.setAttribute("role", "status");
    msgEl.textContent = message;
    formEl.appendChild(msgEl);
  }

  function clearBlockedMessage(formEl) {
    var existing = formEl.querySelector("[data-area-gate-message]");
    if (existing) existing.remove();
  }

  function applyStockGateForCurrentVariant(formEl) {
    var resolved = WA.getResolvedArea();
    if (resolved === WA.NO_AREA_SELECTED) {
      hideSoldOutBadge(formEl); // no area picked yet — nothing to flag
      return;
    }

    var variantId = getCurrentVariantId(formEl);
    var submitBtn = formEl.querySelector(SELECTORS.submitButton);
    var dynamicWrapper = formEl.querySelector(SELECTORS.dynamicCheckoutWrapper);
    var mode = getGateMode(formEl);

    if (!variantId) return;

    // If the variant is genuinely unavailable (not just out of stock in this
    // area), the theme's own sold-out badge/button already communicate that —
    // don't layer a second "Sold out" badge on top of it.
    if (submitBtn && submitBtn.disabled && submitBtn.getAttribute("data-area-gate-disabled") !== "true") {
      hideSoldOutBadge(formEl);
      return;
    }

    var result = resolveStockForVariant(variantId, resolved.warehouse, formEl);

    if (!result.ok) {
      ourEnable(submitBtn);
      hideSoldOutBadge(formEl);
      if (dynamicWrapper) {
        dynamicWrapper.removeAttribute("data-area-gate-disabled");
        if (mode === "label") forceShow(dynamicWrapper);
      }
      return;
    }

    if (result.quantity > 0) {
      ourEnable(submitBtn);
      hideSoldOutBadge(formEl);
      if (dynamicWrapper) {
        dynamicWrapper.removeAttribute("data-area-gate-disabled");
        if (mode === "label") {
          forceShow(dynamicWrapper);
        } else {
          dynamicWrapper.style.removeProperty("pointer-events");
          dynamicWrapper.style.removeProperty("opacity");
        }
      }
      clearBlockedMessage(formEl);
    } else {
      ourDisable(submitBtn);
      showSoldOutBadge(formEl);
      if (dynamicWrapper) {
        dynamicWrapper.setAttribute("data-area-gate-disabled", "true");
        if (mode === "label") {
          forceHide(dynamicWrapper);
        } else {
          dynamicWrapper.style.setProperty("pointer-events", "none");
          dynamicWrapper.style.setProperty("opacity", "0.5");
        }
      }
    }
  }

  function handleFormSubmit(event) {
    var formEl = event.target;
    if (!formEl.matches(SELECTORS.form)) return;

    if (!window.ensureAreaSelected()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var resolved = WA.getResolvedArea();
    var variantId = getCurrentVariantId(formEl);

    if (!variantId) {
      console.warn("[pdp-area-gate] No variant ID found on submit. Failing open.");
      return;
    }

    // CHANGED: added formEl as 3rd argument
    var result = resolveStockForVariant(variantId, resolved.warehouse, formEl);

    if (!result.ok || result.quantity > 0) {
      clearBlockedMessage(formEl);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (getGateMode(formEl) === "message") {
      showBlockedMessage(
        formEl,
        OUT_OF_STOCK_MESSAGE_TEMPLATE.replace("{area_name}", resolved.area_name)
      );
    }
  }

  function wireDynamicCheckoutInterception(formEl) {
    var wrapper = formEl.querySelector(SELECTORS.dynamicCheckoutWrapper);
    if (!wrapper) return;

    wrapper.addEventListener(
      "click",
      function (event) {
        var resolved = WA.getResolvedArea();

        if (resolved === WA.NO_AREA_SELECTED) {
          event.preventDefault();
          event.stopImmediatePropagation();
          window.openAreaModal();
          return;
        }

        var variantId = getCurrentVariantId(formEl);
        if (!variantId) {
          console.warn("[pdp-area-gate] No variant ID found for Buy Now. Failing open.");
          return;
        }

        // CHANGED: added formEl as 3rd argument
        var result = resolveStockForVariant(variantId, resolved.warehouse, formEl);

        if (!result.ok || result.quantity > 0) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (getGateMode(formEl) === "message") {
          showBlockedMessage(
            formEl,
            OUT_OF_STOCK_MESSAGE_TEMPLATE.replace("{area_name}", resolved.area_name)
          );
        }
      },
      true
    );
  }

  function refreshAllForCurrentVariant(formEl) {
    var resolved = WA.getResolvedArea();
    if (resolved === WA.NO_AREA_SELECTED) {
      clearInventoryLabelOverride(formEl);
      updateVariantOptionAvailability(formEl); // clears any stale .disabled marks
      return;
    }

    var variantId = getCurrentVariantId(formEl);
    if (!variantId) return;

    var result = resolveStockForVariant(variantId, resolved.warehouse, formEl);
    if (result.ok) {
      updateInventoryLabel(formEl, result.quantity, resolved.area_name);
    }

    applyStockGateForCurrentVariant(formEl);
    updateVariantOptionAvailability(formEl);
  }

  var _wiredForms = new WeakSet();
  var _allForms = [];

  function wireForm(formEl) {
    if (_wiredForms.has(formEl)) return;
    _wiredForms.add(formEl);
    _allForms.push(formEl);

    refreshAllForCurrentVariant(formEl);
    wireDynamicCheckoutInterception(formEl);

    var variantIdEl = getVariantIdElement(formEl);
    if (!variantIdEl) return;

    variantIdEl.addEventListener("change", function () {
      refreshAllForCurrentVariant(formEl);
    });

    if (variantIdEl.tagName === "INPUT") {
      var lastValue = variantIdEl.value;
      var observer = new MutationObserver(function () {
        if (variantIdEl.value !== lastValue) {
          lastValue = variantIdEl.value;
          refreshAllForCurrentVariant(formEl);
        }
      });
      observer.observe(variantIdEl, { attributes: true, attributeFilter: ["value"] });
    }
  }

  function scanForNewForms(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches(SELECTORS.form)) {
      wireForm(root);
    }
    root.querySelectorAll(SELECTORS.form).forEach(wireForm);
  }

  function getBadgeContainer(el) {
    return el.closest(".product-card, .product__info-container, .ct-item");
  }

  function getBadgeEl(formEl) {
    var container = getBadgeContainer(formEl);
    return container ? container.querySelector("[data-area-gate-badge]") : null;
  }

  function showSoldOutBadge(el) {
    var container = getBadgeContainer(el);
    if (!container) return;

    var badge = container.querySelector("[data-area-gate-badge]");
    if (badge) badge.style.removeProperty("display");

    container
      .querySelectorAll(".f-badge:not([data-area-gate-badge]), .badge:not([data-area-gate-badge])")
      .forEach(function (sibling) {
        if (sibling.style.display === "none") return;
        sibling.setAttribute("data-area-gate-hidden-sibling", "true");
        sibling.style.setProperty("display", "none");
      });
  }

  function hideSoldOutBadge(el) {
    var container = getBadgeContainer(el);
    if (!container) return;

    var badge = container.querySelector("[data-area-gate-badge]");
    if (badge) badge.style.setProperty("display", "none");

    container.querySelectorAll('[data-area-gate-hidden-sibling="true"]').forEach(function (sibling) {
      sibling.style.removeProperty("display");
      sibling.removeAttribute("data-area-gate-hidden-sibling");
    });
  }

  // function wireStandaloneBuyNowButton(btn) {
  //   if (_wiredForms.has(btn)) return;
  //   _wiredForms.add(btn);

  //   var variantId = btn.getAttribute("data-variant-id");
  //   if (!variantId) return;

  //   var resolved = WA.getResolvedArea();
  //   var soldOut = false;

  //   if (resolved !== WA.NO_AREA_SELECTED) {
  //     if (btn.disabled && btn.getAttribute("data-area-gate-disabled") !== "true") {
  //       hideSoldOutBadge(btn); // genuinely unavailable, not our concern
  //     } else {
  //       var result = resolveStockForVariant(variantId, resolved.warehouse, btn);
  //       soldOut = result.ok && result.quantity <= 0;
  //     }
  //   } else {
  //     soldOut = isSoldOutEverywhere([variantId], btn);
  //   }

  //   if (soldOut) {
  //     ourDisable(btn);
  //     showSoldOutBadge(btn);
  //   } else if (!(btn.disabled && btn.getAttribute("data-area-gate-disabled") !== "true")) {
  //     ourEnable(btn);
  //     hideSoldOutBadge(btn);
  //   }

  //   // NEW: mirror this same computed sold-out state onto the sibling
  //   // Add to Cart / Choose Options button on this card. Same computation,
  //   // same trigger points as Buy Now — no separate check, no new listeners.
  //   syncCardAddToCartButton(btn, soldOut);

  //   btn.addEventListener(
  //     "click",
  //     function (event) {
  //       var currentResolved = WA.getResolvedArea();

  //       if (currentResolved !== WA.NO_AREA_SELECTED) {
  //         var result = resolveStockForVariant(variantId, currentResolved.warehouse, btn);
  //         if (result.ok && result.quantity <= 0) {
  //           event.preventDefault();
  //           event.stopImmediatePropagation();
  //           return;
  //         }
  //       } else if (isSoldOutEverywhere([variantId], btn)) {
  //         // Dead stock in every area — don't even bother opening the area
  //         // modal, since picking one won't change the outcome.
  //         event.preventDefault();
  //         event.stopImmediatePropagation();
  //         return;
  //       }

  //       if (!window.ensureAreaSelected()) {
  //         event.preventDefault();
  //         event.stopImmediatePropagation();
  //         return;
  //       }

  //       window.location.href = "/cart/" + variantId + ":1?checkout";
  //     },
  //     true
  //   );
  // }

  function findFirstInStockVariant(variantIds, warehouse, scopeEl) {
    for (var i = 0; i < variantIds.length; i++) {
      var result = resolveStockForVariant(String(variantIds[i]), warehouse, scopeEl);
      if (result.ok && result.quantity > 0) return String(variantIds[i]);
    }
    return null;
  }

  function wireStandaloneBuyNowButton(btn) {
    if (_wiredForms.has(btn)) return;
    _wiredForms.add(btn);

    var variantId = btn.getAttribute("data-variant-id");
    if (!variantId) return;

    // Collect all variant IDs from the warehouse stock JSON on this card.
    // Falls back to the single variant ID if the JSON only has one entry.
    function getAllVariantIds() {
      var data = getStockDataForForm(btn);
      var keys = Object.keys(data);
      return keys.length > 0 ? keys : [variantId];
    }

    var isMultiVariant = false;

    // Determine multi-variant after stock data is available
    (function detectMultiVariant() {
      var data = getStockDataForForm(btn);
      isMultiVariant = Object.keys(data).length > 1;
    })();

    function computeSoldOut(resolved) {
      var ids = getAllVariantIds();
      if (isMultiVariant) {
        // Sold out only if NO variant has stock in the area
        return resolved !== WA.NO_AREA_SELECTED
          ? findFirstInStockVariant(ids, resolved.warehouse, btn) === null
          : isSoldOutEverywhere(ids, btn);
      } else {
        // Original single-variant logic
        if (resolved === WA.NO_AREA_SELECTED) {
          return isSoldOutEverywhere(ids, btn);
        }
        var result = resolveStockForVariant(variantId, resolved.warehouse, btn);
        return result.ok && result.quantity <= 0;
      }
    }

    function applyState() {
      var resolved = WA.getResolvedArea();

      if (btn.disabled && btn.getAttribute("data-area-gate-disabled") !== "true") {
        hideSoldOutBadge(btn); // genuinely unavailable by Shopify — not our concern
        return;
      }

      var soldOut = computeSoldOut(resolved);

      if (soldOut) {
        ourDisable(btn);
        showSoldOutBadge(btn);
      } else {
        ourEnable(btn);
        hideSoldOutBadge(btn);
      }

      syncCardAddToCartButton(btn, soldOut);
    }

    applyState();

    btn.addEventListener(
      "click",
      function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        var resolved = WA.getResolvedArea();

        if (resolved === WA.NO_AREA_SELECTED) {
          // Open area modal; after selection, re-run click logic
          if (!window.ensureAreaSelected()) {
            // ensureAreaSelected opens the modal; wire a one-time listener
            // for when the area gets resolved, then proceed
            document.addEventListener(
              "area:selected",
              function onAreaSelected() {
                document.removeEventListener("area:selected", onAreaSelected);
                var freshResolved = WA.getResolvedArea();
                if (freshResolved === WA.NO_AREA_SELECTED) return;
                applyState();
                var ids = getAllVariantIds();
                var targetId = isMultiVariant
                  ? findFirstInStockVariant(ids, freshResolved.warehouse, btn)
                  : variantId;
                if (!targetId) return; // sold out in this area
                window.location.href = "/cart/" + targetId + ":1?checkout";
              },
              { once: true }
            );
          }
          return;
        }

        // Area is already selected
        var ids = getAllVariantIds();
        var targetId;

        if (isMultiVariant) {
          targetId = findFirstInStockVariant(ids, resolved.warehouse, btn);
          if (!targetId) {
            // All variants sold out in this area — disable and badge
            ourDisable(btn);
            showSoldOutBadge(btn);
            syncCardAddToCartButton(btn, true);
            return;
          }
        } else {
          var result = resolveStockForVariant(variantId, resolved.warehouse, btn);
          if (result.ok && result.quantity <= 0) return; // already disabled, shouldn't reach here
          targetId = variantId;
        }

        window.location.href = "/cart/" + targetId + ":1?checkout";
      },
      true
    );
  }

  // NEW: applies Buy Now's already-computed soldOut boolean to the card's
  // Add to Cart / Choose Options button. Label text is left untouched per
  // requirement — only the disabled state toggles.
  function syncCardAddToCartButton(buyNowBtn, soldOut) {
    var container = getBadgeContainer(buyNowBtn);
    if (!container) return;

    var atcBtn = container.querySelector(SELECTORS.cardAddToCartButton);
    if (!atcBtn) return;

    atcBtn.disabled = !!soldOut;
    if (soldOut) {
      atcBtn.setAttribute("data-area-gate-disabled", "true");
    } else {
      atcBtn.removeAttribute("data-area-gate-disabled");
    }
  }

  function scanForNewBuyNowButtons(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.nodeType === 1 && root.matches && root.matches(".buy-now[data-variant-id]")) {
      wireStandaloneBuyNowButton(root);
    }
    root.querySelectorAll(".buy-now[data-variant-id]").forEach(wireStandaloneBuyNowButton);
  }

  function updateVariantOptionAvailability(formEl) {
    var container = formEl.closest(".product__info-container");
    var variantSelectsEl = container && container.querySelector("variant-selects");
    if (!variantSelectsEl) return;

    var resolved = WA.getResolvedArea();

    var maps = variantSelectsEl.querySelectorAll("script[data-option-variant-map]");
    maps.forEach(function (mapEl) {
      var position = mapEl.getAttribute("data-option-position");
      var fieldset = variantSelectsEl.querySelector(
        '[data-option-position="' + position + '"]:not(script)'
      );
      if (!fieldset) return;

      var valueMap;
      try {
        valueMap = JSON.parse(mapEl.textContent || "{}");
      } catch (err) {
        console.error("[area-gate] Failed to parse option-variant map.", err);
        return;
      }

      Object.keys(valueMap).forEach(function (value) {
        var variantIds = valueMap[value];
        var soldOutForArea = false;

        if (variantIds.length > 0) {
          if (resolved !== WA.NO_AREA_SELECTED) {
            soldOutForArea = variantIds.every(function (variantId) {
              var result = resolveStockForVariant(String(variantId), resolved.warehouse, formEl);
              return result.ok && result.quantity <= 0;
            });
          } else {
            soldOutForArea = isSoldOutEverywhere(variantIds, formEl);
          }
        }

        var decodedValue = document.createElement('textarea');
        decodedValue.innerHTML = value;
        var decodedStr = decodedValue.value;
        fieldset.querySelectorAll('input, option').forEach(function (optionEl) {
          if (optionEl.value !== decodedStr) return;
          if (optionEl.tagName === "OPTION") {
            if (soldOutForArea) {
              if (!optionEl.hasAttribute("data-area-gate-original-text")) {
                optionEl.setAttribute("data-area-gate-original-text", optionEl.textContent);
              }
              optionEl.textContent =
                optionEl.getAttribute("data-area-gate-original-text") + " – Sold out";
            } else if (optionEl.hasAttribute("data-area-gate-original-text")) {
              optionEl.textContent = optionEl.getAttribute("data-area-gate-original-text");
              optionEl.removeAttribute("data-area-gate-original-text");
            }
          } else {
            optionEl.classList.toggle("disabled", soldOutForArea);
          }
        });
      });
    });
  }

  function isSoldOutEverywhere(variantIds, formEl) {
    var areas = WA.getAvailableAreas() || [];
    if (areas.length === 0 || variantIds.length === 0) return false;

    return variantIds.every(function (variantId) {
      return areas.every(function (area) {
        var result = resolveStockForVariant(String(variantId), area.warehouse, formEl);
        return result.ok && result.quantity <= 0;
      });
    });
  }

  function init() {
    scanForNewForms(document);
    scanForNewBuyNowButtons(document); // ADDED

    var bodyObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) {
            scanForNewForms(node);
            scanForNewBuyNowButtons(node); // ADDED
          }
        });
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    document.addEventListener("variant:change", function () {
      _allForms.forEach(function (formEl) {
        if (document.body.contains(formEl)) {
          refreshAllForCurrentVariant(formEl);
        }
      });
    });

    document.addEventListener("submit", handleFormSubmit, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
